import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./GlanceAdmin.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const TOKEN_KEY = "young-knowledge-admin-token";

async function api(path, { token, method = "GET", body, formData } = {}) {
  const headers = {};
  if (token) headers["X-Knowledge-Token"] = token;
  if (body !== undefined && !formData) headers["Content-Type"] = "application/json";

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: formData || (body !== undefined ? JSON.stringify(body) : undefined),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

const GlanceAdmin = () => {
  const [status, setStatus] = useState(null);
  const [tokenInput, setTokenInput] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("gallery");

  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [href, setHref] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");

  const [rulesText, setRulesText] = useState("");
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [userMessage, setUserMessage] = useState("");
  const [agentAnswer, setAgentAnswer] = useState("");
  const [feedback, setFeedback] = useState("");
  const [applyKnowledge, setApplyKnowledge] = useState(true);
  const [testQuery, setTestQuery] = useState("");
  const [testResult, setTestResult] = useState(null);

  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/glance/status`).then((r) => r.json()),
      fetch(`${API_URL}/api/knowledge/status`).then((r) => r.json()),
    ])
      .then(([glance, knowledge]) =>
        setStatus({
          enabled: knowledge?.enabled,
          cloudinary: glance?.cloudinary,
        })
      )
      .catch(() => setStatus({ enabled: false }));
  }, []);

  const loadGallery = useCallback(async () => {
    const data = await api("/api/glance");
    setItems(data.items || []);
  }, []);

  const loadAgent = useCallback(async (authToken) => {
    const data = await api("/api/agent-tuning", { token: authToken });
    setRules(data.rules || []);
    setRulesText((data.rules || []).join("\n"));
    setHistory(data.history || []);
  }, []);

  useEffect(() => {
    loadGallery().catch(() => {});
  }, [loadGallery]);

  useEffect(() => {
    if (!token) return;
    loadAgent(token).catch((err) => {
      setMessage({ type: "err", text: err.message });
    });
  }, [token, loadAgent]);

  const unlock = () => {
    const t = tokenInput.trim();
    if (!t) return;
    sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setMessage(null);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f ? URL.createObjectURL(f) : "");
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (!file) {
      setMessage({ type: "err", text: "Choose an image first." });
      return;
    }
    if (!title.trim()) {
      setMessage({ type: "err", text: "Title is required (shown in the gallery)." });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("title", title.trim());
      if (href.trim()) formData.append("href", href.trim());
      await api("/api/glance/upload", { token, method: "POST", formData });
      await loadGallery();
      setTitle("");
      setHref("");
      setFile(null);
      if (preview) URL.revokeObjectURL(preview);
      setPreview("");
      setMessage({ type: "ok", text: "Uploaded: live on the homepage gallery now." });
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const removeGalleryItem = async (id) => {
    if (!token) return;
    if (!window.confirm("Remove this image from the gallery?")) return;
    setBusy(true);
    setMessage(null);
    try {
      await api(`/api/glance/${encodeURIComponent(id)}`, { token, method: "DELETE" });
      await loadGallery();
      setMessage({ type: "ok", text: "Removed." });
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const saveRules = async () => {
    if (!token) return;
    setBusy(true);
    setMessage(null);
    try {
      const list = rulesText
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      const data = await api("/api/agent-tuning/rules", { token, method: "PUT", body: { rules: list } });
      setRules(data.rules || list);
      setMessage({ type: "ok", text: "Agent rules saved. Digital Young uses them immediately." });
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const deleteRule = async (index) => {
    if (!token) return;
    setBusy(true);
    setMessage(null);
    try {
      const data = await api(`/api/agent-tuning/rules/${index}`, { token, method: "DELETE" });
      setRules(data.rules || []);
      setRulesText((data.rules || []).join("\n"));
      setMessage({ type: "ok", text: "Rule removed." });
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setMessage(null);
    setTestResult(null);
    try {
      const data = await api("/api/agent-tuning/feedback", {
        token,
        method: "POST",
        body: {
          userMessage: userMessage.trim(),
          agentAnswer: agentAnswer.trim(),
          feedback: feedback.trim(),
          applyKnowledge,
        },
      });
      const r = data.result;
      await loadAgent(token);
      const parts = [
        r.ruleAdded ? `Added rule: “${r.appliedRule}”` : "Rule already existed (skipped duplicate).",
        r.knowledgeId ? `Updated knowledge record “${r.knowledgeId}”.` : null,
        r.note,
      ].filter(Boolean);
      setMessage({ type: "ok", text: parts.join(" ") });
      setFeedback("");
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    if (!token) return;
    setBusy(true);
    setMessage(null);
    setTestResult(null);
    try {
      const data = await api("/api/agent-tuning/test", {
        token,
        method: "POST",
        body: { text: testQuery.trim() },
      });
      setTestResult(data);
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="ga-page">
        <div className="ga-wrap">Loading…</div>
      </div>
    );
  }

  return (
    <div className="ga-page">
      <div className="ga-wrap ga-wrap--wide">
        <header className="ga-head">
          <div>
            <h1>Admin</h1>
            <p>
              Gallery uploads, Digital Young rules, and one-click fixes from bad replies. Token matches{" "}
              <Link to="/knowledge">knowledge base</Link>.
            </p>
          </div>
          <div className="ga-head__links">
            <Link to="/agent" className="ga-btn ga-btn--ghost">
              Open agent →
            </Link>
            <Link to="/" className="ga-btn ga-btn--ghost">
              Site →
            </Link>
          </div>
        </header>

        {!status.enabled && (
          <div className="ga-panel ga-msg ga-msg--err">
            Set <code>KNOWLEDGE_ADMIN_TOKEN</code> in <code>server/.env</code> (and on Render), restart the API, then
            reload.
          </div>
        )}

        {status.enabled && !token && (
          <div className="ga-panel ga-gate">
            <label htmlFor="ga-token">Admin token</label>
            <input
              id="ga-token"
              type="password"
              autoComplete="off"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && unlock()}
              placeholder="KNOWLEDGE_ADMIN_TOKEN"
            />
            <button type="button" className="ga-btn" onClick={unlock}>
              Unlock
            </button>
          </div>
        )}

        {token && (
          <nav className="ga-tabs" aria-label="Admin sections">
            <button type="button" className={tab === "gallery" ? "is-active" : ""} onClick={() => setTab("gallery")}>
              Gallery
            </button>
            <button type="button" className={tab === "agent" ? "is-active" : ""} onClick={() => setTab("agent")}>
              Agent rules
            </button>
            <button type="button" className={tab === "fix" ? "is-active" : ""} onClick={() => setTab("fix")}>
              Fix a bad reply
            </button>
            <button type="button" className={tab === "test" ? "is-active" : ""} onClick={() => setTab("test")}>
              Test reply
            </button>
          </nav>
        )}

        {message ? (
          <p className={`ga-msg ga-msg--${message.type === "ok" ? "ok" : "err"}`}>{message.text}</p>
        ) : null}

        {token && tab === "gallery" && (
          <>
            {status.cloudinary ? (
              <p className="ga-note">Gallery storage: Cloudinary.</p>
            ) : (
              <p className="ga-note ga-note--warn">Add Cloudinary on the API for persistent gallery uploads.</p>
            )}
            <form className="ga-panel ga-upload" onSubmit={upload}>
              <h2>Upload to Work at a glance</h2>
              <div className="ga-upload__grid">
                <label className="ga-field">
                  <span>Image</span>
                  <input type="file" accept="image/*" onChange={onPickFile} disabled={busy} />
                </label>
                <label className="ga-field">
                  <span>Title</span>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Collabstr: checkout refresh"
                    disabled={busy}
                  />
                </label>
                <label className="ga-field">
                  <span>Link (optional)</span>
                  <input
                    type="url"
                    value={href}
                    onChange={(e) => setHref(e.target.value)}
                    placeholder="https://…"
                    disabled={busy}
                  />
                </label>
              </div>
              {preview ? <img src={preview} alt="" className="ga-upload__preview" /> : null}
              <button type="submit" className="ga-btn" disabled={busy}>
                {busy ? "Uploading…" : "Add to gallery"}
              </button>
            </form>
            <section className="ga-panel">
              <h2>Uploaded via admin ({items.length})</h2>
              {items.length === 0 ? (
                <p className="ga-empty">No admin uploads yet.</p>
              ) : (
                <ul className="ga-list">
                  {items.map((item) => (
                    <li key={item.id}>
                      <img src={item.src} alt="" />
                      <div className="ga-list__meta">
                        <strong>{item.title}</strong>
                        {item.href ? (
                          <a href={item.href} target="_blank" rel="noreferrer">
                            {item.href}
                          </a>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="ga-btn ga-btn--danger"
                        disabled={busy}
                        onClick={() => removeGalleryItem(item.id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}

        {token && tab === "agent" && (
          <>
            <section className="ga-panel">
              <h2>Extra rules for Digital Young</h2>
              <p className="ga-hint">
                One rule per line. These append to the system prompt with highest priority. Use for tone, boundaries,
                or phrasing you do not want to edit in Cursor.
              </p>
              <textarea
                className="ga-textarea"
                rows={12}
                value={rulesText}
                onChange={(e) => setRulesText(e.target.value)}
                placeholder="When asked about X, lead with Y…&#10;Never say Z…"
                disabled={busy}
              />
              <button type="button" className="ga-btn" disabled={busy} onClick={saveRules}>
                Save rules
              </button>
            </section>
            {rules.length > 0 && (
              <section className="ga-panel">
                <h2>Active rules ({rules.length})</h2>
                <ul className="ga-rules">
                  {rules.map((rule, i) => (
                    <li key={`${i}-${rule.slice(0, 24)}`}>
                      <span>{rule}</span>
                      <button type="button" className="ga-btn ga-btn--danger" disabled={busy} onClick={() => deleteRule(i)}>
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        {token && tab === "fix" && (
          <>
            <form className="ga-panel" onSubmit={submitFeedback}>
              <h2>Fix a bad reply</h2>
              <p className="ga-hint">
                Paste what the visitor asked, what the agent said, and what you want instead. The API drafts a new rule
                and, when needed, a knowledge record, then applies both so the next conversation improves.
              </p>
              <label className="ga-field">
                <span>Visitor message</span>
                <textarea
                  className="ga-textarea"
                  rows={3}
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  disabled={busy}
                />
              </label>
              <label className="ga-field">
                <span>What the agent said (wrong)</span>
                <textarea
                  className="ga-textarea"
                  rows={4}
                  value={agentAnswer}
                  onChange={(e) => setAgentAnswer(e.target.value)}
                  disabled={busy}
                />
              </label>
              <label className="ga-field">
                <span>Your feedback</span>
                <textarea
                  className="ga-textarea"
                  rows={3}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Should sound more confident / mention IC permissions / don't claim I led eng…"
                  disabled={busy}
                />
              </label>
              <label className="ga-check">
                <input
                  type="checkbox"
                  checked={applyKnowledge}
                  onChange={(e) => setApplyKnowledge(e.target.checked)}
                  disabled={busy}
                />
                Also add or update a knowledge record when this is a factual fix
              </label>
              <button type="submit" className="ga-btn" disabled={busy}>
                {busy ? "Applying…" : "Apply fix automatically"}
              </button>
            </form>
            {history.length > 0 && (
              <section className="ga-panel">
                <h2>Recent fixes</h2>
                <ul className="ga-history">
                  {history.slice(0, 8).map((h) => (
                    <li key={h.id}>
                      <time dateTime={h.at}>{new Date(h.at).toLocaleString()}</time>
                      <p className="ga-history__rule">{h.appliedRule}</p>
                      <p className="ga-history__note">{h.note}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        {token && tab === "test" && (
          <section className="ga-panel">
            <h2>Test a question</h2>
            <p className="ga-hint">Runs the same pipeline as <Link to="/agent">/agent</Link> with current rules and corpus.</p>
            <div className="ga-test-row">
              <input
                type="text"
                value={testQuery}
                onChange={(e) => setTestQuery(e.target.value)}
                placeholder="Are you a senior designer?"
                disabled={busy}
                onKeyDown={(e) => e.key === "Enter" && runTest()}
              />
              <button type="button" className="ga-btn" disabled={busy || !testQuery.trim()} onClick={runTest}>
                Run
              </button>
            </div>
            {testResult && (
              <div className="ga-test-out">
                <p>
                  <strong>Reply:</strong> {testResult.spoken_answer}
                </p>
                {testResult.evidence?.length ? (
                  <p>
                    <strong>Evidence:</strong> {testResult.evidence.map((e) => e.title).join(" · ")}
                  </p>
                ) : null}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default GlanceAdmin;
